import { ComponentProps, forwardRef, useEffect, useRef } from 'react';
import composeRefs from '@seznam/compose-react-refs';
import { animate, transform, useMotionValue } from 'motion/react';

import { cn } from '@components/primitives/utilities';

import css from './styles.module.css';

export type ShimmerTextProps = ComponentProps<'span'>;

/**
 * Make your text shimmer.
 *
 * - Use `--shimmer-color` to set the color of the shimmer.
 * - Use `--shimmer-opacity` to set the opacity of the shimmer.
 */
export const ShimmerText = forwardRef<HTMLSpanElement, ShimmerTextProps>(
  function ShimmerText({ className, ...props }, ref) {
    const innerRef = useRef<HTMLSpanElement>(null);
    const progress = useMotionValue(0);

    useEffect(() => {
      progress.set(0);
      const controls = animate(progress, 100, {
        repeat: Infinity,
        repeatType: 'loop',
        duration: 2,
        ease: 'linear',
        onUpdate(latest) {
          const value = transform(latest, [0, 100], [-100, 100]);
          innerRef.current?.style.setProperty(
            '--shimmer-progress',
            value.toString()
          );
        },
      });

      return () => controls.stop();
    }, []);

    return (
      <span
        ref={composeRefs(ref, innerRef)}
        className={cn(className, css.label)}
        {...props}
      />
    );
  }
);
